import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from 'environments/environment';
import { SessionService } from './session.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  token: string | undefined;

  constructor(
    private msalService: MsalService,
    private sessionService: SessionService
  ) {}

  private get account(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  isLoginRedirected(): boolean {
    return window.location.hash.includes('code=');
  }

  isLoggedIn(): boolean {
    return this.account != null;
  }

  /**
   * Confirms a cached MSAL account still has a live session by silently
   * refreshing its token, instead of trusting the cached account alone.
   * Covers the "closed browser / reopened from a bookmark" case where a
   * stale account can remain in localStorage after the session/refresh
   * token is no longer valid. Any failure (InteractionRequiredAuthError or
   * otherwise) routes through the centralized SessionService so the user is
   * redirected to Microsoft Entra ID login rather than left on a stale page.
   */
  async validateSession(): Promise<boolean> {
    const account = this.account ?? this.msalService.instance.getAllAccounts()[0] ?? null;

    if (!account) {
      return false;
    }

    if (!this.account) {
      this.msalService.instance.setActiveAccount(account);
    }

    try {
      await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: environment.APP_SCOPES,
      });
      return true;
    } catch (error) {
      console.warn('MSAL session validation failed; ending session', error);
      this.sessionService.handleSessionTimeout();
      return false;
    }
  }

  isAdmin(): boolean {
    const roles = this.account?.idTokenClaims?.roles as string[] | undefined;
    if (!roles) {
      return false;
    }
    return roles.includes('Admin');
  }

  isAccountant(): boolean {
    const roles = this.account?.idTokenClaims?.roles as string[] | undefined;
    if (!roles) {
      return false;
    }
    return !roles.includes('Admin') && roles.includes('Accountant');
  }

  isUnderwriter(): boolean {
    const roles = this.account?.idTokenClaims?.roles as string[] | undefined;
    if (!roles) {
      return false;
    }
    return !roles.includes('Admin') && roles.includes('Underwriter');
  }

  getUserRole(): string | null {
    if (this.isAdmin()) {
      return 'Admin';
    } else if (this.isAccountant()) {
      return 'Accountant';
    } else if (this.isUnderwriter()) {
      return 'Underwriter';
    } else {
      return null;
    }
  }

  getUserName(): string | null {
    return this.account?.name || null;
  }

  login() {
    if (!this.isLoggedIn()) {
      this.msalService.loginRedirect({
        scopes: environment.APP_SCOPES,
        prompt: 'consent'
      });
    }
  }

  logout() {
    if (this.isLoggedIn()) {
      sessionStorage.clear();
      this.msalService.logoutRedirect().subscribe();
    }
  }
}
